import { useEffect, useState } from 'react'
import { useRpc } from './people.ts'

/** The E-Phy reference (M21, docs/EPHY.md): what `api.ephy_search` returns (pgTAP 54 pins its keys: change them together). It is national public data, read online,
 *  never kept on the device, linked to nothing of the club; the screen recommends nothing and computes no dose. Pure functions here, with their tests (ephy.test.ts). */
export interface EphyUsage {
  label: string; state: string; decided_on: string | null; dose: number | null; dose_unit: string | null; max_applications: number | null; min_interval_days: number | null
  znt_aquatic_m: number | null; znt_arthropods_m: number | null; znt_plants_m: number | null; conditions: string | null; end_of_distribution: string | null; end_of_use: string | null
}
export interface EphyProduct {
  amm: string; name: string; other_names: string | null; holder: string | null; product_type: string; state: string; usage_range: string | null; mentions: string[]
  biocontrol: boolean; low_risk: boolean; organic: boolean; substances: string | null; functions: string | null; withdrawn_on: string | null
  /** the server's fact: the product is authorised AND has at least one turf usage whose state begins "Autorisé" */
  turf_authorised: boolean; turf_usages: EphyUsage[]
}
export interface EphySource { file_name: string; published_on: string; loaded_at: string; licence: string; source_url: string; products: number; turf_usages: number }
export interface EphySearch { source: EphySource | null; total: number; limit: number; products: EphyProduct[] }

/** The screen's three lists. */
export const EPHY_FILTERS = ['golf', 'turf_banned', 'all'] as const
export type EphyFilter = (typeof EPHY_FILTERS)[number]
export type GolfClass = Exclude<EphyFilter, 'all'> | null
/** The flags the exemption names (biocontrol, low risk, usable in organic farming), in the order the screen shows them. */
export const EPHY_FLAGS = ['biocontrol', 'low_risk', 'organic'] as const
export const EPHY_MIN_QUERY = 2
export const EPHY_TURF_LIMIT = 200
export const EPHY_ALL_LIMIT = 50

/** THE rule of the two turf lists (the only place it is written): a product authorised on turf is usable on tees, greens and fairways since 2025 when it carries one of
 *  the three mentions, and banned there otherwise (outside the list of exceptions for official competitions, which the screen does not compute). It is the screen's reading
 *  of the law, shown with "(à vérifier)": never a recommendation. A product with no authorised turf usage is in neither list. */
export function golfClass(p: EphyProduct): GolfClass {
  if (!p.turf_authorised) return null
  return p.biocontrol || p.low_risk || p.organic ? 'golf' : 'turf_banned'
}

export const filterEphy = (rows: readonly EphyProduct[], filter: EphyFilter): EphyProduct[] =>
  filter === 'all' ? [...rows] : rows.filter((p) => golfClass(p) === filter)

/** How many of the rows each turf list holds (shown on the filter buttons). */
export function ephyCounts(rows: readonly EphyProduct[]): Record<Exclude<EphyFilter, 'all'>, number> {
  const out = { golf: 0, turf_banned: 0 }
  for (const p of rows) { const c = golfClass(p); if (c) out[c]++ }
  return out
}

/** "Tous les produits" searches all of E-Phy (any state, any crop): it needs a query of two characters; the turf lists show everything without one. */
export const needsQuery = (query: string, filter: EphyFilter): boolean => filter === 'all' && query.trim().length < EPHY_MIN_QUERY

/** The RPC's arguments: the turf lists ask for the authorised turf products (both lists come from one answer), "all" for any product. */
export function ephyArgs(org: string, query: string, filter: EphyFilter): { p_org: string; p_query: string; p_turf_only: boolean; p_limit: number } {
  const turf = filter !== 'all'
  return { p_org: org, p_query: query.trim(), p_turf_only: turf, p_limit: turf ? EPHY_TURF_LIMIT : EPHY_ALL_LIMIT }
}

export const isAuthorisedUsage = (u: EphyUsage): boolean => u.state.startsWith('Autorisé')
export const ephyFlags = (p: EphyProduct): Array<(typeof EPHY_FLAGS)[number]> => EPHY_FLAGS.filter((f) => p[f])

/** A usage label as E-Phy writes it ("Gazons de graminées*Trt Part.Aer.*Dollar spot") without the crop every row here shares: "Trt Part.Aer. › Dollar spot". */
export function usageText(label: string): string {
  const parts = label.split('*').map((s) => s.trim()).filter(Boolean)
  return (parts.length > 1 ? parts.slice(1) : parts).join(' › ')
}

/** The figures of a usage as the file gives them, one sentence each, in the order of the row; `n` writes a number for the reader. A count is a plural key (M2):
 *  the interval is a number of days, decimals possible ("1,5 jour"), so it is the key's count. */
export function usageFacts(u: EphyUsage, t: (k: string, o?: Record<string, unknown>) => string, n: (x: number) => string): string[] {
  return [
    u.dose !== null && t('ephy.usage.dose', { dose: n(u.dose), unit: u.dose_unit ?? '' }),
    u.max_applications !== null && t('ephy.usage.maxApplications', { count: u.max_applications }),
    u.min_interval_days !== null && t('ephy.usage.interval', { count: u.min_interval_days, days: n(u.min_interval_days) }),
    u.znt_aquatic_m !== null && t('ephy.usage.zntAquatic', { m: n(u.znt_aquatic_m) }),
    u.znt_arthropods_m !== null && t('ephy.usage.zntArthropods', { m: n(u.znt_arthropods_m) }),
    u.znt_plants_m !== null && t('ephy.usage.zntPlants', { m: n(u.znt_plants_m) }),
  ].filter((x): x is string => Boolean(x))
}

/** The screen's rows (M23, docs/M23.md item 42: a search used to print 50 fully open cards, ~19 000 CSS px on a phone). A row is closed until tapped;
 *  the list shows a page of EPHY_PAGE rows and "Afficher plus" for the rest; and with no query on a turf list the screen gives the count and a hint,
 *  the whole list one tap away, instead of every product. */
export const EPHY_PAGE = 20
export function ephyPage<T>(rows: readonly T[], shown: number): { rows: T[]; hidden: number } {
  const n = Math.max(0, Math.min(rows.length, Math.floor(shown)))
  return { rows: rows.slice(0, n), hidden: rows.length - n }
}
/** No query at all: the person has not asked for anything yet, so the list waits behind its count. */
export const browseOnly = (query: string): boolean => query.trim() === ''
/** What a closed row says of its uses: every turf usage the file lists for the product (authorised or withdrawn), as the open row shows them. */
export const usageCount = (p: EphyProduct): number => p.turf_usages.length

/** The query a person types, applied once they stop typing (the search is an online read). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id) }, [value, ms])
  return v
}

export function useEphySearch(org: string, query: string, filter: EphyFilter) {
  const enabled = !needsQuery(query, filter)
  return useRpc<EphySearch>('ephy_search', ephyArgs(org, query, filter), enabled)
}
